import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductDetailPage from "./pages/ProductDetailPage";
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderReceivedPage = lazy(() => import("./pages/OrderReceivedPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const QuoteRequestPage = lazy(() => import("./pages/QuoteRequestPage"));
const AdminConsolePage = lazy(() => import("./pages/AdminConsolePage"));
const AdminOrderDetailPage = lazy(() => import("./pages/AdminOrderDetailPage"));
const AdminChatConsole = lazy(() => import("./pages/AdminChatConsole"));
import ChatWidget from "./components/ChatWidget";
const AccountingPage = lazy(() => import("./pages/AccountingPage"));
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const SameDayPage = lazy(() => import("./pages/SameDayPage"));
const HangTagsLandingPage = lazy(() => import("./pages/HangTagsLandingPage"));
const TicketsPage = lazy(() => import("./pages/TicketsPage"));
const TentsLandingPage = lazy(() => import("./pages/CategoryLandingPage").then((m) => ({ default: m.TentsLandingPage })));
const ProofReviewPage = lazy(() => import("./pages/ProofReviewPage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));
import LocalBusinessSchema from "./components/LocalBusinessSchema";
const IndustriesPage = lazy(() => import("./pages/IndustriesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ToolPage = lazy(() => import("./pages/ToolPage"));
const FoilChartPage = lazy(() => import("./pages/FoilChartPage"));
const EmbedToolPage = lazy(() => import("./pages/EmbedToolPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const ReviewSubmitPage = lazy(() => import("./pages/ReviewSubmitPage"));
const UseCasePage = lazy(() => import("./pages/UseCasePage"));
const UseCaseIndexPage = lazy(() => import("./pages/UseCaseIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/legal/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const RefundPolicyPage = lazy(() => import("./pages/legal/RefundPolicyPage"));
import ScrollToTop from "./components/ScrollToTop";
import TrafficTracker from "./components/TrafficTracker";
import { Toaster } from "./components/ui/sonner";
import { MobileBottomNav } from "./components/mobile/MobileBottomNav";
import { useIsMobile, mobileNavHidden } from "./hooks/useIsMobile";

function AppRouter() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const mobileNav = isMobile && !mobileNavHidden(location.pathname);
  // Detect Emergent Google session_id in URL fragment (synchronous during render)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Marketing + tickets have their own full-screen take-over chrome.
  const hideChrome = location.pathname.startsWith("/marketing") || location.pathname.startsWith("/tickets");
  // /embed/* renders a single tool for third-party iframes — no chrome at all.
  if (location.pathname.startsWith("/embed/")) {
    return (
      <Suspense fallback={<div className="min-h-[50vh]" aria-busy="true" data-testid="route-loading" />}>
        <Routes>
          <Route path="/embed/tools/:toolId" element={<EmbedToolPage />} />
          <Route path="*" element={<Navigate to="/resources" replace />} />
        </Routes>
      </Suspense>
    );
  }
  return (
    <div className={mobileNav ? "pb-16" : undefined} data-mobile-layout={isMobile ? "1" : undefined}>
      <ScrollToTop />
      <TrafficTracker />
      <LocalBusinessSchema />
      {!hideChrome && <Navbar />}
      <Suspense fallback={<div className="min-h-[50vh]" aria-busy="true" data-testid="route-loading" />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/:service_category" element={<ShopPage />} />
        <Route path="/shop/:service_category/:sub_category" element={<ShopPage />} />
        <Route path="/shop/product/:slug" element={<ProductDetailPage />} />
        {/* Legacy product URL — 301-style client redirect so any indexed
            `/product/:slug` links keep working after the move to /shop/product/. */}
        <Route path="/product/:slug" element={<LegacyProductRedirect />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/order-received" element={<OrderReceivedPage />} />
        <Route path="/login" element={<LoginPage mode="login" />} />
        <Route path="/register" element={<LoginPage mode="register" />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/:section" element={<DashboardPage />} />
        <Route path="/quote" element={<QuoteRequestPage />} />
        <Route path="/admin" element={<AdminConsolePage />} />
        <Route path="/admin/chat" element={<AdminChatConsole />} />
        <Route path="/admin/order/:order_id" element={<AdminOrderDetailPage />} />
        <Route path="/accounting" element={<AccountingPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/marketing/:section" element={<MarketingPage />} />
        <Route path="/same-day" element={<SameDayPage />} />
        <Route path="/hang-tags" element={<HangTagsLandingPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/large-format/tents" element={<TentsLandingPage />} />
        <Route path="/proof/:order_id/:token" element={<ProofReviewPage />} />
        <Route path="/invoice/:order_id/:token" element={<InvoicePage />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/tools/foil-color-chart" element={<FoilChartPage />} />
        <Route path="/tools/:toolId" element={<ToolPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/review/:token" element={<ReviewSubmitPage />} />
        <Route path="/labels" element={<UseCaseIndexPage basePath="/labels" />} />
        <Route path="/labels/:useCase" element={<UseCasePage basePath="/labels" />} />
        <Route path="/large-format" element={<UseCaseIndexPage basePath="/large-format" />} />
        <Route path="/large-format/:useCase" element={<UseCasePage basePath="/large-format" />} />
        <Route path="/tools" element={<Navigate to="/resources" replace />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
      </Suspense>
      {!hideChrome && <Footer />}
      <ChatWidget />
      <MobileBottomNav />
    </div>
  );
}

function LegacyProductRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/shop/product/${slug}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
